import React from "react";
import { HashRouter as Router } from "react-router-dom";
import { ThemeProvider, createTheme, responsiveFontSizes, CssBaseline } from "@mui/material";
import { UseCasesProvider, type UseCases } from "@gui/contexts/useCases/UseCasesContext";
import { authUseCases } from "@gui/infrastructure/composition/auth";
import { companiesUseCases } from "@gui/infrastructure/composition/companies";
import { importSettingsUseCases } from "@gui/infrastructure/composition/imports";
import { integrationsUseCases } from "@gui/infrastructure/composition/integrations";
import { missionControlUseCases } from "@gui/infrastructure/composition/missionControl";
import { passwordGeneratorUseCases } from "@gui/infrastructure/composition/teamMembers";
import { labelsUseCases } from "@gui/infrastructure/composition/labels";
import { missionsUseCases } from "@gui/infrastructure/composition/missions";
import { duplicateDealsUseCases } from "@gui/infrastructure/composition/deals";
import { branchMatcherUseCases } from "@gui/infrastructure/composition/branchMatcher";
import { dealMatcherUseCases } from "@gui/infrastructure/composition/dealMatcher";
import { jobUseCases } from "@gui/infrastructure/composition/jobs";
import { shiftsUseCases } from "@gui/infrastructure/composition/shifts";
import { UserContext } from "@gui/contexts/user/UserContext";
import { CompanyContextProvider } from "@gui/contexts/company";
import { IssueCountsProvider } from "@gui/contexts/issueCounts/IssueCountsContext";
import { User } from "@domain/Auth";
const useCases: UseCases = {
	auth: authUseCases,
	companies: companiesUseCases,
	jobs: jobUseCases,
	shifts: shiftsUseCases,
	missions: missionsUseCases,
	branchMatcher: branchMatcherUseCases,
	dealMatcher: dealMatcherUseCases,
	importSettings: importSettingsUseCases,
	duplicateDeals: duplicateDealsUseCases,
	integrations: integrationsUseCases,
	missionControl: missionControlUseCases,
	passwordGenerator: passwordGeneratorUseCases,
	labels: labelsUseCases,
};

/**
 * Application theme — the single source of truth for design tokens.
 *
 * `cssVariables: true` (MUI v6) makes `createTheme` emit `--mui-palette-*` (and
 * typography/spacing) CSS custom properties on `:root` and expose `theme.vars.*`.
 * The ~15 component CSS files consume those generated vars directly, which is what
 * retired the hand-maintained `tokens.css` duplicate (design-audit §3, §8).
 *
 * This is a FOUNDATION: values are chosen to preserve the current rendered
 * appearance. The semantic palette slots (success/warning/error/info) match the
 * hexes already hardcoded across the app (design-audit §2.2/§2.4) so that the
 * later palette-adoption task (customer-portal-2vuc) is a no-op recolor — nothing
 * consumes these slots from the theme yet, so defining them changes nothing today.
 */
const baseTheme = createTheme({
	cssVariables: true,
	palette: {
		primary: {
			main: "#274052",
		},
		secondary: {
			main: "#dc004e",
		},
		// Semantic slots — values match colors already in use (design-audit §2.4).
		error: {
			main: "#d32f2f", // == former --color-error / MUI default error.main
			dark: "#b71c1c", // == ERROR_STROKE (OpsMap.layers)
		},
		warning: {
			main: "#ed6c02", // "// warning.main" annotation in DealCard
			light: "#ff9800", // age-status / map warning orange
		},
		success: {
			main: "#4caf50", // #4caf50 (12 occ) → success.main per §2.4
			dark: "#2e7d32", // "// success.dark" annotation in DealCard
			light: "#66bb6a",
		},
		info: {
			main: "#1976d2", // UI "current/active" blue accent (RobotList, §2.2)
		},
	},
	// Typography — anchor the family/weights so a scale exists to lean on. Variant
	// sizes stay at MUI defaults (appearance-preserving); responsiveFontSizes below
	// folds in the mobile-heading scaling the app currently fakes with inline ternaries.
	typography: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,
	},
	// Spacing — MUI's default 8px scale; the app already uses it implicitly via `sx`
	// numbers (design-audit §4). Stated explicitly to make it a deliberate decision.
	spacing: 8,
	// Shape — 4px is MUI's default and the app's most common rendered radius
	// (buttons/inputs/selects; design-audit §6). Held constant to avoid a restyle.
	shape: {
		borderRadius: 4,
	},
	components: {
		// The app is a pure client-rendered SPA with 36+ useMediaQuery call sites
		// (design-audit §3). noSsr skips the first-paint pass where every query
		// resolves false, removing a layout flash. Final render is identical.
		MuiUseMediaQuery: {
			defaultProps: { noSsr: true },
		},
		// Outlined is the app-wide text-field convention and MUI's default — anchored
		// here so it is a decision, not an accident (design-audit §6.1). No-op today.
		MuiTextField: {
			defaultProps: { variant: "outlined" },
		},
		// Status chips are used at size="small" across the app (JobOverviewCard,
		// flag chips, DealPropertyChip; design-audit §3). Making it the default lets
		// those call sites drop the repeated prop over time.
		MuiChip: {
			defaultProps: { size: "small" },
		},
	},
});

export const theme = responsiveFontSizes(baseTheme);

interface AppProvidersProps {
	currentUser: User | undefined;
	children: React.ReactNode;
}

/**
 * Composition root for the app's React provider tree.
 *
 * Order is significant (outermost → innermost):
 * - UseCasesProvider: outermost so any consumer (including auth flows that run before a user
 *   exists) can invoke use cases without depending on UserContext.
 * - UserContext: above ThemeProvider so any consumer can read currentUser without a CompanyContext.
 * - ThemeProvider: wraps everything that uses MUI styling. It also injects the
 *   generated `--mui-*` CSS variables (theme.cssVariables) that component CSS consumes.
 * - CssBaseline: normalizes browser defaults (margin/box-sizing) and drives the body
 *   background/text from the theme. Rendered once, directly under ThemeProvider.
 * - Router: wraps everything that uses routing (must be inside ThemeProvider since some MUI
 *   components rely on the theme inside route elements).
 * - CompanyContextProvider: depends on currentUser and is consumed by feature pages, so it
 *   sits inside Router and UserContext.
 * - IssueCountsProvider: scoped to authenticated feature pages and may depend on the active
 *   company, so it sits innermost around children.
 */
export function AppProviders({ currentUser, children }: AppProvidersProps) {
	return (
		<UseCasesProvider value={useCases}>
			<UserContext.Provider value={currentUser}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
						<CompanyContextProvider user={currentUser}>
							<IssueCountsProvider>{children}</IssueCountsProvider>
						</CompanyContextProvider>
					</Router>
				</ThemeProvider>
			</UserContext.Provider>
		</UseCasesProvider>
	);
}
